/**
 * Safely handles ActiveCampaign proc.php responses without eval or JSONP.
 */
import { parse } from "acorn";
import { Sentry } from "./sentryInstrument";
import "./i18nFallback";

const PROC_PHP_PATTERN = /activehosted\.com\/proc\.php/;
const PROC_ENDPOINT =
  "https://reviveourhearts.activehosted.com/proc.php?jsonp=true";

const FAILURE_MESSAGE = window.gettext(
  "Sorry, your submission failed. Please try again."
);
const LONG_PAYLOAD_FAILURE_MESSAGE = window.gettext(
  "Sorry, your submission failed. Please shorten your responses and try again."
);

// The only ActiveCampaign callbacks we replay.
const NOTIFICATION_DEFAULT_MESSAGES = new Map([
  ["_show_thank_you", window.gettext("Thank you for subscribing!")],
  ["_show_error", FAILURE_MESSAGE],
  ["_show_unsubscribe", ""],
  ["_show_pc_confirmation", ""],
]);

const UNSAFE = Symbol("unsafe");
const SENTRY_JS_MAX_LENGTH = 500;

function truncateForSentry(value, maxLength = SENTRY_JS_MAX_LENGTH) {
  if (typeof value !== "string") {
    return value;
  }
  return value.length <= maxLength
    ? value
    : `${value.slice(0, maxLength)}...`;
}

function sentryContext(formId, js) {
  return {
    extra: {
      formId,
      js: truncateForSentry(js),
    },
  };
}

// Accept only static values with no runtime behavior.
function staticValue(node) {
  switch (node.type) {
    case "Literal":
      return node.regex ? UNSAFE : node.value;
    case "Identifier":
      return node.name === "undefined" ? undefined : UNSAFE;
    case "TemplateLiteral":
      if (node.expressions.length > 0) {
        return UNSAFE;
      }
      return node.quasis.map((quasi) => quasi.value.cooked).join("");
    case "UnaryExpression": {
      if (!node.prefix || (node.operator !== "-" && node.operator !== "+")) {
        return UNSAFE;
      }
      const value = staticValue(node.argument);
      if (typeof value !== "number") {
        return UNSAFE;
      }
      return node.operator === "-" ? -value : value;
    }
    default:
      return UNSAFE;
  }
}

// Allows `_show_*()` and `window._show_*()` forms.
function notificationName(callee) {
  let name = null;
  if (callee.type === "Identifier") {
    name = callee.name;
  } else if (
    callee.type === "MemberExpression" &&
    !callee.computed &&
    callee.object.type === "Identifier" &&
    callee.object.name === "window" &&
    callee.property.type === "Identifier"
  ) {
    name = callee.property.name;
  }
  return NOTIFICATION_DEFAULT_MESSAGES.has(name) ? name : null;
}

/**
 * @param {string} js
 * @returns {{calls: {name: string, args: unknown[]}[], parseError: Error | null}}
 */
function analyzeProcResponse(js) {
  let program;
  let parseError = null;
  try {
    program = parse(js, { ecmaVersion: "latest" });
  } catch (error) {
    parseError = error instanceof Error ? error : new Error(String(error));
    return { calls: [], parseError };
  }

  const calls = [];
  for (const statement of program.body) {
    if (
      statement.type !== "ExpressionStatement" ||
      statement.expression.type !== "CallExpression"
    ) {
      continue;
    }
    const call = statement.expression;
    const name = notificationName(call.callee);
    if (!name) {
      continue;
    }
    const args = call.arguments.map((argument) =>
      argument.type === "SpreadElement" ? UNSAFE : staticValue(argument)
    );
    if (!args.includes(UNSAFE)) {
      calls.push({ name, args });
    }
  }
  return { calls, parseError: null };
}

/** @param {string} js @returns {{name: string, args: unknown[]}[]} */
function extractNotificationCalls(js) {
  return analyzeProcResponse(js).calls;
}

function resetSubmitButton(formId) {
  if (!formId) {
    return;
  }
  const button = document.querySelector(`#_form_${formId}_submit`);
  if (button) {
    button.disabled = false;
    button.classList.remove("processing");
  }
}

function showError(formId, message) {
  if (typeof window._show_error === "function" && formId) {
    window._show_error(formId, message);
  }
}

// Replays a safe callback with local defaults.
function dispatchNotification(name, args, formId) {
  const fn = window[name];
  if (typeof fn !== "function") {
    return;
  }
  const finalArgs = [...args];
  finalArgs[0] ||= formId;
  finalArgs[1] ||= NOTIFICATION_DEFAULT_MESSAGES.get(name);
  fn(...finalArgs);
}

/**
 * @param {unknown} data
 * @param {string} fallbackFormId
 * @param {boolean} [isSubmit]
 */
function handleResponse(data, fallbackFormId, isSubmit) {
  const js = typeof data?.js === "string" ? data.js.trim() : "";
  const formId = fallbackFormId || "";

  if (!js) {
    dispatchNotification("_show_thank_you", [], formId);
    return;
  }

  const { calls, parseError } = analyzeProcResponse(js);
  if (parseError) {
    Sentry.captureException(parseError, {
      ...sentryContext(formId, js),
      tags: { stage: "activecampaign proc.php parse" },
    });
    showError(formId, FAILURE_MESSAGE);
    if (isSubmit) {
      resetSubmitButton(formId);
    }
    return;
  }

  if (calls.length > 0) {
    for (const { name, args } of calls) {
      dispatchNotification(name, args, formId);
    }
    return;
  }

  Sentry.captureMessage(
    "ActiveCampaign proc.php response contained no safe notification calls",
    {
      level: "warning",
      ...sentryContext(formId, js),
    }
  );
  showError(formId, FAILURE_MESSAGE);
  if (isSubmit) {
    resetSubmitButton(formId);
  }
}

/**
 * @param {string} url
 * @param {(() => void) | null | undefined} callback
 * @param {boolean | undefined} isSubmit
 */
async function safeProcPhpSubmit(url, callback, isSubmit) {
  const params = new URLSearchParams(url.slice(url.indexOf("?") + 1));
  const formId = params.get("f") || params.get("u") || "";
  const formData = new FormData();
  params.forEach((value, key) => {
    if (key !== "hideButton") {
      formData.append(key, value);
    }
  });

  const headers = { Accept: "application/json" };
  const pageUrlParams = new URLSearchParams(window.location.search);
  if (pageUrlParams.has("t")) {
    headers.Authorization = `Bearer ${pageUrlParams.get("t")}`;
  }

  try {
    const response = await fetch(PROC_ENDPOINT, {
      method: "POST",
      headers,
      body: formData,
    });
    const data = await response.json();
    handleResponse(data, formId, isSubmit);
    callback?.();
  } catch (error) {
    Sentry.captureException(error, {
      extra: { formId },
      tags: { stage: "activecampaign proc.php fetch" },
    });
    showError(
      formId,
      url.length > 10000 ? LONG_PAYLOAD_FAILURE_MESSAGE : FAILURE_MESSAGE
    );
    resetSubmitButton(formId);
  }
}

function installOverride() {
  const original = window._load_script;
  window._load_script = function (url, callback, isSubmit) {
    if (typeof url === "string" && PROC_PHP_PATTERN.test(url)) {
      return safeProcPhpSubmit(url, callback, isSubmit);
    }
    if (typeof original === "function") {
      return original.apply(this, arguments);
    }
  };
}

window.activeCampaignSafeSubmit = {
  handleResponse,
  extractNotificationCalls,
  install: installOverride,
};

installOverride();
